import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Input } from './Input';
import { CalendarPicker } from './CalendarPicker';
import { cn } from '@/shared/lib/utils';
import { useUserPreferencesSafe } from '@/shared/providers/UserPreferencesProvider';
import { getServerCalendarDate, formatServerDate } from '@/shared/utils/serverTimeContext';

interface DatePickerWithCalendarProps {
  id?: string;
  label?: React.ReactNode;
  /** Accessible label for the input — used when no visible label is rendered (e.g. inside a FormField). Forwarded to the inner <input> aria-label. */
  'aria-label'?: string;
  value: string;
  onChange: (value: string) => void;
  min?: string | undefined;
  max?: string | undefined;
  required?: boolean;
  disabled?: boolean;
  error?: string | undefined;
  className?: string;
  'data-testid'?: string;
  formatDate?: (dateStr: string) => string;
  /** When true, the underlying text input receives focus on mount (mirrors HTML autoFocus). */
  autoFocus?: boolean;
  /**
   * Override the week start day shown in the calendar grid.
   * When omitted the component reads `profile.view.start_dow` from
   * `UserPreferencesProvider` and falls back to `'sunday'`.
   */
  startDow?: 'sunday' | 'monday';
}

// ─── Short month lookup (lower-case) ────────────────────────────────────────
const SHORT_MONTHS = [
  'jan', 'feb', 'mar', 'apr', 'may', 'jun',
  'jul', 'aug', 'sep', 'oct', 'nov', 'dec',
];

// ─── Locale-independent "D MMM YYYY" display formatter ───────────────────────
const MONTHS_DISPLAY = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/**
 * Convert "YYYY-MM-DD" string → local-tz Date (avoids UTC-midnight off-by-one).
 * Returns null for any invalid / empty input.
 */
const stringToDate = (dateStr: string): Date | null => {
  if (!dateStr) return null;
  const isoDate = dateStr.split('T')[0];
  const parts = isoDate.split('-');
  if (parts.length !== 3) return null;
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  const day = parseInt(parts[2], 10);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
  const date = new Date(year, month - 1, day);
  return isNaN(date.getTime()) ? null : date;
};

/** Convert Date → "YYYY-MM-DD" string. */
const dateToString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Format a "YYYY-MM-DD" string as "13 May 2026" for display.
 * Falls back to '' when the string is absent or unparseable.
 */
const formatDisplayDate = (dateStr: string): string => {
  if (!dateStr) return '';
  const date = stringToDate(dateStr);
  if (!date) return '';
  return `${date.getDate()} ${MONTHS_DISPLAY[date.getMonth()]} ${date.getFullYear()}`;
};

/**
 * Attempt to parse any of several date formats the user might type and
 * return a normalised "YYYY-MM-DD" string, or null when the input cannot
 * be understood.
 *
 * Supported formats (all case-insensitive):
 *  • ISO:            "2026-05-13"  /  "2026-5-3"
 *  • D MMM YYYY:     "13 May 2026" / "13 may 2026"
 *  • MM/DD/YYYY:     "06/05/2026"  (primary — "06/05/2026" → June 5)
 *  • DD/MM/YYYY:     "13/05/2026"  (only when the first part > 12, unambiguous)
 *  • MMDDYYYY:       "06052026"    (8-digit compact)
 *
 * Month ambiguity (e.g. "06/05/2026"): we default to MM/DD/YYYY (en-US),
 * so "06/05/2026" → June 5 2026.
 */
const parseTypedDate = (raw: string, minBound?: Date, maxBound?: Date): string | null => {
  const s = raw.trim();
  if (!s) return null;

  /** Returns true when the given ISO string is within the caller-supplied range. */
  const inRange = (iso: string): boolean => {
    const d = stringToDate(iso);
    if (!d) return false;
    if (minBound && d < minBound) return false;
    if (maxBound && d > maxBound) return false;
    return true;
  };

  // ── ISO: YYYY-MM-DD ────────────────────────────────────────────────────────
  const isoMatch = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (isoMatch) {
    const [, y, m, d] = isoMatch;
    const date = new Date(Number(y), Number(m) - 1, Number(d));
    if (!isNaN(date.getTime()) && date.getMonth() === Number(m) - 1) {
      return dateToString(date);
    }
    return null;
  }

  // ── ISO variant with slashes: YYYY/MM/DD ──────────────────────────────────
  const isoSlash = s.match(/^(\d{4})[/](\d{1,2})[/](\d{1,2})$/);
  if (isoSlash) {
    const [, y, m, d] = isoSlash;
    const date = new Date(Number(y), Number(m) - 1, Number(d));
    if (!isNaN(date.getTime()) && date.getMonth() === Number(m) - 1) {
      return dateToString(date);
    }
    return null;
  }

  // ── D MMM YYYY  or  D MMMM YYYY ───────────────────────────────────────────
  const textMatch = s.match(/^(\d{1,2})\s+([a-zA-Z]+)\s+(\d{2,4})$/);
  if (textMatch) {
    const [, dStr, monStr, yStr] = textMatch;
    const monthIdx = SHORT_MONTHS.indexOf(monStr.toLowerCase().slice(0, 3));
    if (monthIdx !== -1) {
      const year = yStr.length === 2 ? 2000 + Number(yStr) : Number(yStr);
      const date = new Date(year, monthIdx, Number(dStr));
      if (!isNaN(date.getTime()) && date.getMonth() === monthIdx) {
        return dateToString(date);
      }
    }
    return null;
  }

  // ── Compact 8-digit: YYYYMMDD (ISO) checked BEFORE DDMMYYYY to avoid
  //    ambiguity — year segment starts with a 4-digit century (19xx/20xx).
  //    Guard uses the raw `s` (not the stripped `compact`) so that
  //    slash/dot/dash-separated inputs like "06/05/2026" are NOT swallowed
  //    here — they must fall through to the slash-match branch which applies
  //    the correct MM/DD/YYYY-first convention.
  const compact = s.replace(/\D/g, '');
  if (/^\d{8}$/.test(s)) {
    // Try YYYYMMDD first (compact ISO — common in spreadsheet/system exports)
    const cy = parseInt(compact.slice(0, 4), 10);
    const cm = parseInt(compact.slice(4, 6), 10);
    const cd = parseInt(compact.slice(6, 8), 10);
    if (cy > 1900 && cy < 2100) {
      const isoDate = new Date(cy, cm - 1, cd);
      if (!isNaN(isoDate.getTime()) && isoDate.getMonth() === cm - 1 && isoDate.getDate() === cd) {
        return dateToString(isoDate);
      }
    }

    // Try DDMMYYYY
    const d = parseInt(compact.slice(0, 2), 10);
    const m = parseInt(compact.slice(2, 4), 10);
    const y = parseInt(compact.slice(4, 8), 10);
    const date = new Date(y, m - 1, d);
    if (!isNaN(date.getTime()) && date.getMonth() === m - 1 && date.getDate() === d) {
      return dateToString(date);
    }
    // try MMDDYYYY
    const dm = parseInt(compact.slice(0, 2), 10);
    const dd = parseInt(compact.slice(2, 4), 10);
    const date2 = new Date(y, dm - 1, dd);
    if (!isNaN(date2.getTime()) && date2.getMonth() === dm - 1 && date2.getDate() === dd) {
      return dateToString(date2);
    }
    return null;
  }

  // ── D/M/YYYY, DD/MM/YYYY, D.M.YYYY, D-M-YYYY etc. ────────────────────────
  const slashMatch = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})$/);
  if (slashMatch) {
    const [, aStr, bStr, yStr] = slashMatch;
    const year = yStr.length === 2 ? 2000 + Number(yStr) : Number(yStr);
    const a = Number(aStr);
    const b = Number(bStr);

    // When a > 12, it MUST be the day → DD/MM/YYYY (unambiguous)
    if (a > 12) {
      const date = new Date(year, b - 1, a);
      if (!isNaN(date.getTime()) && date.getMonth() === b - 1 && date.getDate() === a) {
        return dateToString(date);
      }
      return null;
    }

    // When b > 12, it MUST be the day → MM/DD/YYYY (unambiguous)
    if (b > 12) {
      const date = new Date(year, a - 1, b);
      if (!isNaN(date.getTime()) && date.getMonth() === a - 1 && date.getDate() === b) {
        return dateToString(date);
      }
      return null;
    }

    // Ambiguous: both a ≤ 12 and b ≤ 12.
    // Primary:  MM/DD/YYYY — "06/05/2026" → June 5 (en-US convention used by this app).
    // Fallback: DD/MM/YYYY — tried only when range bounds are present and the
    //           MM/DD result falls outside them.
    const mmddDate = new Date(year, a - 1, b);
    const mmddIso =
      !isNaN(mmddDate.getTime()) && mmddDate.getMonth() === a - 1 && mmddDate.getDate() === b
        ? dateToString(mmddDate)
        : null;

    const ddmmDate = new Date(year, b - 1, a);
    const ddmmIso =
      !isNaN(ddmmDate.getTime()) && ddmmDate.getMonth() === b - 1 && ddmmDate.getDate() === a
        ? dateToString(ddmmDate)
        : null;

    if (minBound !== undefined || maxBound !== undefined) {
      if (mmddIso && inRange(mmddIso)) return mmddIso;
      if (ddmmIso && inRange(ddmmIso)) return ddmmIso;
      // Neither fits — return primary (MM/DD) so caller can show the range-error message.
      return mmddIso ?? ddmmIso ?? null;
    }

    // No range context: MM/DD/YYYY default.
    return mmddIso ?? null;
  }

  return null;
};

export const DatePickerWithCalendar: React.FC<DatePickerWithCalendarProps> = ({
  id,
  label,
  'aria-label': ariaLabel,
  value,
  onChange,
  min,
  max,
  required = false,
  disabled = false,
  error,
  className = '',
  'data-testid': dataTestId,
  formatDate,
  autoFocus = false,
  startDow,
}) => {
  const { t } = useTranslation('translation');

  // Read the user's week-start preference from context.
  // useUserPreferencesSafe returns null when rendered outside a provider
  // (e.g. unit tests / Storybook) — in that case we fall back to 'monday',
  // matching Rails SYSTEM_DEFAULT_VALUES.start_dow and the mf_start_dow_monday?
  // helper which treats absent/nil start_dow as monday.
  const prefsCtx = useUserPreferencesSafe();
  const prefStartDow = prefsCtx?.preferences?.profile?.view?.start_dow;
  const effectiveStartDow: 'sunday' | 'monday' =
    startDow ?? (prefStartDow === 'sunday' ? 'sunday' : 'monday');

  // ── Calendar open state ───────────────────────────────────────────────────
  const [isOpen, setIsOpen] = useState(false);
  // Track whether the calendar was opened via keyboard so we can move focus
  // into the portal calendar (for keyboard-only and screen reader users).
  // Mouse-click opens should NOT steal focus from the input.
  const openedViaKeyboardRef = useRef(false);

  // ── Typing-mode state ─────────────────────────────────────────────────────
  /** When the user focuses the input we switch to "editing" mode.
   *  `inputText`  = the raw characters the user has typed.
   *  `parseError` = message shown inline beneath the input when we cannot
   *                 understand what was typed; cleared on blur once we
   *                 normalise or revert. */
  const [isEditing, setIsEditing] = useState(false);
  const [inputText, setInputText] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const calendarPortalRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Tracks whether a mousedown is currently in progress inside the calendar
  // portal. Used as a fallback in onBlur handlers when relatedTarget is null
  // (can happen in Firefox/Safari when clicking inside a portal).
  const calendarMouseDownRef = useRef(false);
  // Start hidden — position is computed in updateCalendarPosition (called inside the
  // isOpen useEffect). Keeping visibility:'hidden' on the first frame prevents the
  // portal from flashing at (0,0) before the effect fires and sets the real coords.
  const [calendarStyle, setCalendarStyle] = useState<React.CSSProperties>({ visibility: 'hidden' });

  const selectedDate = stringToDate(value);
  const minDate = min ? stringToDate(min) || undefined : undefined;
  const maxDate = max ? stringToDate(max) || undefined : undefined;

  // ── Display value for the input ───────────────────────────────────────────
  const displayValue: string = isEditing
    ? inputText
    : (formatDate ? formatDate(value) : formatDisplayDate(value));

  // ─────────────────────────────────────────────────────────────────────────
  // Calendar position helpers
  // ─────────────────────────────────────────────────────────────────────────

  const updateCalendarPosition = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const calendarHeight = 320;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUpward = spaceBelow < calendarHeight && rect.top > calendarHeight;

    setCalendarStyle({
      position: 'fixed',
      zIndex: 9999,
      width: rect.width,
      left: rect.left,
      visibility: 'visible',   // reveal only after position is known — eliminates the first-paint flash
      ...(openUpward
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
    });
  }, []);

  // ── Focus management: move focus into the calendar portal on keyboard open ──
  // WCAG 2.1 §2.1.1 Keyboard / §2.4.3 Focus Order:
  // When the calendar opens via keyboard (ArrowDown/F4 on input, Enter/Space on
  // the icon button), focus MUST move into the portal calendar so keyboard-only
  // and screen reader users can select a date without having to Tab into the
  // portal manually (the portal is DOM-disconnected from the trigger).
  // Mouse-initiated opens do NOT steal focus from the input.
  useEffect(() => {
    if (!isOpen || !openedViaKeyboardRef.current) return;
    // NOTE: Do NOT reset openedViaKeyboardRef.current here — it must stay `true`
    // for the entire time the calendar is open so the Tab guard below can check it.
    // It is reset to `false` only when the calendar closes (see the effect below).
    // requestAnimationFrame waits for the portal to render and be positioned
    // before attempting to move focus into it.
    requestAnimationFrame(() => {
      if (!calendarPortalRef.current) return;
      // The CalendarPicker uses a roving-tabIndex pattern: the focused/selected
      // date cell always has tabIndex="0". Find that button first, then fall back
      // to the first non-disabled button (e.g. a nav arrow).
      const rovingFocused = calendarPortalRef.current.querySelector<HTMLButtonElement>(
        '[role="grid"] button[tabindex="0"]',
      );
      const first = calendarPortalRef.current.querySelector<HTMLButtonElement>(
        'button:not([disabled])',
      );
      (rovingFocused ?? first)?.focus();
    });
  }, [isOpen]);

  // Reset the keyboard-open flag when the calendar closes so mouse-initiated
  // reopens don't inherit a stale `true` from a prior keyboard-open.
  useEffect(() => {
    if (!isOpen) {
      openedViaKeyboardRef.current = false;
    }
  }, [isOpen]);

  // ── Click-outside / Escape / scroll-resize listeners ─────────────────────
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const inTrigger = containerRef.current?.contains(target);
      const inPortal = calendarPortalRef.current?.contains(target);
      if (!inTrigger && !inPortal) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Use capture phase (registered below with useCapture=true) so this fires
        // BEFORE the Modal's bubble-phase ESC handler. stopImmediatePropagation()
        // prevents the Modal from also closing when only the calendar should close.
        event.preventDefault();
        event.stopImmediatePropagation();
        setIsOpen(false);
      }
    };
    const handleScrollOrResize = () => {
      if (isOpen) updateCalendarPosition();
    };

    if (isOpen) {
      updateCalendarPosition();
      document.addEventListener('mousedown', handleClickOutside);
      // Capture phase: fires before Modal's bubble-phase handler
      document.addEventListener('keydown', handleKeyDown, true);
      window.addEventListener('scroll', handleScrollOrResize, true);
      window.addEventListener('resize', handleScrollOrResize);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [isOpen, updateCalendarPosition]);

  // ─────────────────────────────────────────────────────────────────────────
  // Input interaction handlers
  // ─────────────────────────────────────────────────────────────────────────

  const handleInputFocus = (): void => {
    if (disabled) return;
    // Enter editing mode: seed the text with the current display value so
    // the user can edit in-place rather than starting from blank.
    setInputText(formatDate ? formatDate(value) : formatDisplayDate(value));
    setIsEditing(true);
    setParseError(null);
    // NOTE: do NOT call setIsOpen(false) here — closing the calendar on
    // every focus event would prevent date cells from being selectable,
    // because clicking a date button shifts focus to that button (triggering
    // this handler) BEFORE the button's click/mouseup fires.
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const raw = e.target.value;
    setInputText(raw);
    setParseError(null);
    // Close the calendar as soon as the user starts typing so it never
    // obscures the field while they are entering a date manually.
    setIsOpen(false);

    // Live parse — if we get a clean ISO string and it is within bounds,
    // fire onChange immediately so parent state stays in sync.
    if (!raw.trim()) {
      onChange('');
      return;
    }
    const parsed = parseTypedDate(raw, minDate, maxDate);
    if (parsed) {
      // Always propagate a parseable date during live typing so the parent
      // state stays in sync. Range violations are validated by the parent
      // (which passes its own `error` prop); the picker must not swallow the
      // value just because it is outside the min/max bounds.
      onChange(parsed);
    }
  };

  const handleInputBlur = (): void => {
    setIsEditing(false);
    const raw = inputText.trim();

    if (!raw) {
      onChange('');
      setParseError(null);
      return;
    }

    const parsed = parseTypedDate(raw, minDate, maxDate);
    if (!parsed) {
      // Revert to the last valid value if the user typed garbage.
      setParseError(t('shared.datePicker.invalidDate', 'Invalid date — try "13 May 2026" or MM/DD/YYYY'));
      // We keep the bad text visible for a moment then restore the display value
      // on next render (isEditing = false → displayValue uses `value`).
      return;
    }

    // Only commit a parseable date that is within the allowed range.
    // Out-of-range dates set a parse error and return without calling onChange
    // so the parent's value stays at the last valid date. This preserves the
    // picker's role as a range guard: parents that do not independently
    // re-validate the submitted value will never receive an out-of-range date.
    const parsedDate = stringToDate(parsed)!;
    if (minDate && parsedDate < minDate) {
      setParseError(t('shared.datePicker.dateTooEarly', 'Date is before the allowed minimum'));
      return;
    }
    if (maxDate && parsedDate > maxDate) {
      setParseError(t('shared.datePicker.dateTooLate', 'Date is after the allowed maximum'));
      return;
    }

    setParseError(null);
    onChange(parsed);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      // Commit on Enter by programmatically blurring the input.
      // The onBlur handler (handleInputBlur) fires exactly once from the blur
      // event — we do NOT call handleInputBlur() manually here to avoid a
      // double-invocation that would call onChange twice for valid dates.
      e.preventDefault();
      setIsOpen(false);
      inputRef.current?.blur(); // onBlur → handleInputBlur fires exactly once
    }
    if (e.key === 'Escape') {
      // Revert to the last confirmed value.
      // stopImmediatePropagation prevents both React-synthetic and native document
      // listeners (including the Modal's ESC handler) from also firing, so only
      // the date-picker handles this Escape — the modal stays open.
      e.stopPropagation();
      e.nativeEvent.stopImmediatePropagation();
      setIsEditing(false);
      setParseError(null);
      setIsOpen(false);
      inputRef.current?.blur();
    }
    if (e.key === 'ArrowDown' || e.key === 'F4') {
      // Open the calendar via keyboard and move focus into it.
      e.preventDefault();
      setIsEditing(false);
      openedViaKeyboardRef.current = true;
      setIsOpen(true);
    }
    // When the calendar is open AND was opened via keyboard (ArrowDown/F4), Tab
    // from the input moves focus into the calendar grid (WCAG disclosure-widget
    // pattern). When opened via mouse-click, Tab leaves the picker normally so
    // mixed mouse+keyboard users are not trapped inside the calendar.
    if (e.key === 'Tab' && !e.shiftKey && isOpen && openedViaKeyboardRef.current) {
      e.preventDefault();
      const rovingFocused = calendarPortalRef.current?.querySelector<HTMLButtonElement>(
        '[role="grid"] button[tabindex="0"]',
      );
      const first = calendarPortalRef.current?.querySelector<HTMLButtonElement>(
        'button:not([disabled])',
      );
      (rovingFocused ?? first)?.focus();
    }
  };

  const handleCalendarIconKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      // Mark as keyboard-initiated so focus moves into the calendar portal.
      openedViaKeyboardRef.current = true;
    }
    // When the calendar is open AND was opened via keyboard (Enter/Space), Tab
    // from the icon button moves focus into the calendar grid.
    // When opened via mouse-click, Tab leaves the picker normally.
    if (e.key === 'Tab' && !e.shiftKey && isOpen && openedViaKeyboardRef.current) {
      e.preventDefault();
      const rovingFocused = calendarPortalRef.current?.querySelector<HTMLButtonElement>(
        '[role="grid"] button[tabindex="0"]',
      );
      const first = calendarPortalRef.current?.querySelector<HTMLButtonElement>(
        'button:not([disabled])',
      );
      (rovingFocused ?? first)?.focus();
    }
  };

  const handleCalendarIconClick = (): void => {
    if (disabled) return;
    // Commit any pending typed text first.
    if (isEditing) {
      handleInputBlur();
    }
    setIsOpen((prev) => !prev);
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Calendar selection / footer handlers
  // ─────────────────────────────────────────────────────────────────────────

  const handleDateSelect = (date: Date): void => {
    onChange(dateToString(date));
    setParseError(null);
    setIsOpen(false);
    // WCAG 2.1 §2.4.3 Focus Order: return focus to the text input after a date
    // is selected from the calendar portal. Without this, focus is lost to
    // document.body when the portal unmounts, and the user cannot Tab to the
    // next field naturally. requestAnimationFrame waits for the portal to
    // unmount before attempting to focus the input.
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  };

  const handleTodayClick = (): void => {
    // Use server calendar date (not browser UTC) so the "Today" button is
    // consistent with filter anchors across all user time zones.
    const serverDate = getServerCalendarDate();
    const todayStr = formatServerDate(serverDate);
    const todayDate = stringToDate(todayStr);
    if (!todayDate) return;
    if (minDate && todayDate < minDate) return;
    if (maxDate && todayDate > maxDate) return;
    onChange(todayStr);
    setParseError(null);
    setIsOpen(false);
    // Return focus to the input after "Today" is selected (same WCAG reason as handleDateSelect).
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  };

  const handleClearClick = (): void => {
    onChange('');
    setParseError(null);
    setIsOpen(false);
    // Return focus to the input after clearing so keyboard users remain on the field.
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  };

  const isTodayDisabled = (): boolean => {
    const serverDate = getServerCalendarDate();
    const todayStr = formatServerDate(serverDate);
    const todayDate = stringToDate(todayStr);
    if (!todayDate) return false;
    if (minDate && todayDate < minDate) return true;
    if (maxDate && todayDate > maxDate) return true;
    return false;
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────

  // Combine the external `error` prop with any inline parse feedback.
  // parseError takes priority while the user is actively typing/fixing input.
  // Once parseError clears (null), the parent-supplied `error` prop is shown
  // again — this is intentional: a prop error (e.g. form "required") should
  // re-surface as soon as the user's live input is deemed valid by the picker.
  const displayError = parseError ?? error;

  return (
    <div
      className={cn('relative', className)}
      ref={containerRef}
      onBlur={(e) => {
        // Guide §2: close the calendar when focus moves outside the component
        // (e.g. Tab to the next field). Must check both the trigger container
        // AND the portal calendar element (calendarPortalRef).
        // Also skip when a calendar button mousedown is in progress (cross-browser
        // fallback for when relatedTarget is null inside a portal).
        if (calendarMouseDownRef.current) return;
        const target = e.relatedTarget as Node | null;
        if (
          !containerRef.current?.contains(target) &&
          !calendarPortalRef.current?.contains(target)
        ) {
          setIsOpen(false);
          // Also exit editing mode to avoid showing stale typed text
          if (isEditing) {
            handleInputBlur();
          }
        }
      }}
    >
      {label && (
        <label
          htmlFor={id}
          className="block text-sm font-medium text-text mb-1"
          title={typeof label === 'string' ? label : undefined}
        >
          {label}
          {required && <span className="text-error ml-1">*</span>}
        </label>
      )}
      <div className="relative">
        <Input
          id={id}
          ref={inputRef}
          type="text"
          role="combobox"
          value={displayValue}
          disabled={disabled}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
          autoComplete="off"
          onFocus={handleInputFocus}
          onChange={handleInputChange}
          onBlur={(e) => {
            // Only commit typed text on blur when focus moves completely outside
            // the picker (container + calendar portal). If the user clicked a
            // calendar date button, relatedTarget will be that button (inside
            // calendarPortalRef) so we skip handleInputBlur(). We also check
            // calendarMouseDownRef as a fallback for browsers (Firefox/Safari)
            // where relatedTarget may be null when focus moves into a portal.
            if (calendarMouseDownRef.current) return;
            const target = e.relatedTarget as Node | null;
            if (!containerRef.current?.contains(target) && !calendarPortalRef.current?.contains(target)) {
              handleInputBlur();
            }
          }}
          onKeyDown={handleInputKeyDown}
          placeholder={t('shared.datePicker.placeholder', 'e.g. 13 May 2026')}
          className={cn(
            'pr-10',
            displayError ? 'border-error' : '',
            disabled ? 'cursor-not-allowed' : 'cursor-text',
          )}
          aria-label={ariaLabel ?? (typeof label === 'string' ? label : undefined)}
          aria-describedby={displayError ? `${id ?? 'datepicker'}-error` : undefined}
          aria-expanded={isOpen}
          aria-haspopup="dialog"
          aria-autocomplete="none"
          data-testid={dataTestId || 'date-picker-input'}
        />
        {/* Calendar toggle icon
            WCAG 2.1 §2.4.7 Focus Visible: the button must have a visible focus
            ring so keyboard and AT users can see it receive focus.
            WCAG 2.1 §4.1.2 Name, Role, Value: aria-expanded communicates the
            open/closed state of the calendar popup to screen readers.
            The fallback string on aria-label guards against a missing i18n key. */}
        <button
          type="button"
          className={cn(
            'absolute right-2 top-1/2 -translate-y-1/2',
            'pointer-events-auto bg-transparent border-0 p-1 rounded-md',
            'cursor-pointer',
            'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1',
            disabled ? 'cursor-not-allowed opacity-50' : '',
          )}
          onClick={handleCalendarIconClick}
          onKeyDown={handleCalendarIconKeyDown}
          disabled={disabled}
          aria-label={t('shared.datePicker.openCalendar', 'Open calendar')}
          aria-expanded={isOpen}
          aria-haspopup="dialog"
          tabIndex={0}
        >
          <svg
            className={cn(
              'w-5 h-5 transition-colors',
              disabled ? 'text-text-disabled' : 'text-text-muted',
            )}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
        </button>
      </div>

      {/* Error / parse feedback */}
      {displayError && (
        <p
          id={`${id ?? 'datepicker'}-error`}
          className="text-error text-sm mt-1"
          role="alert"
        >
          {displayError}
        </p>
      )}

      {/* Calendar Dropdown — rendered via Portal to escape sidebar overflow/clip */}
      {isOpen && !disabled && createPortal(
        // Outer presentational wrapper handles the onMouseDown focus-guard.
        // role="presentation" removes it from the accessibility tree so jsx-a11y
        // does not require a keyboard handler for the mouse event.
        <div
          ref={calendarPortalRef}
          role="presentation"
          style={calendarStyle}
          onMouseDown={(e) => {
            // Prevent the input from losing focus when the user clicks anywhere
            // in the calendar panel (including the background between buttons).
            // We must NOT call preventDefault() when the target is a <button>
            // (date cells, nav arrows, Clear, Today) — doing so would suppress
            // the native click event in some browsers.
            if (!(e.target as HTMLElement).closest('button')) {
              e.preventDefault();
            } else {
              // Target IS a button: set the ref flag so that the input's onBlur
              // and the container's onBlur (both of which fire before onClick)
              // know to skip calling handleInputBlur(). Clear it on mouseup so
              // the flag doesn't linger after the click completes.
              calendarMouseDownRef.current = true;
              const clearFlag = () => { calendarMouseDownRef.current = false; };
              window.addEventListener('mouseup', clearFlag, { once: true });
            }
          }}
        >
          {/* Inner div carries the ARIA dialog semantics. Separated from the outer
              onMouseDown wrapper so jsx-a11y does not flag a landmark role element
              with a mouse event handler (role="dialog" is a landmark, not a widget).
              tabIndex={-1} makes it programmatically focusable (not in tab order itself)
              so the useEffect focus logic can call .focus() on it if needed, and
              onFocus delegates immediately to the roving-tabIndex date button. */}
          <div
            role="dialog"
            aria-label={t('shared.datePicker.calendarDialog', 'Date picker calendar')}
            aria-modal="false"
            data-testid={`${dataTestId || 'date-picker'}-calendar`}
            tabIndex={-1}
            onFocus={(e) => {
              // When focus lands on the dialog container itself (not on a child button),
              // delegate immediately to the roving-tabIndex date cell so the user
              // doesn't need an extra Tab/Enter to reach the grid.
              if (e.target === e.currentTarget) {
                const rovingFocused = calendarPortalRef.current?.querySelector<HTMLButtonElement>(
                  '[role="grid"] button[tabindex="0"]',
                );
                const first = calendarPortalRef.current?.querySelector<HTMLButtonElement>(
                  'button:not([disabled])',
                );
                (rovingFocused ?? first)?.focus();
              }
            }}
          >
            <CalendarPicker
              value={selectedDate}
              onChange={handleDateSelect}
              {...(minDate !== undefined && { minDate })}
              {...(maxDate !== undefined && { maxDate })}
              startDow={effectiveStartDow}
              data-testid={`${dataTestId || 'date-picker'}-calendar`}
            />
            {/* ── Clear / Today footer ──────────────────────────────────────────── */}
            <div className="bg-surface rounded-b-lg shadow-lg border-t border-border px-3 py-2 flex items-center justify-between w-[280px]">
              <button
                type="button"
                onClick={handleClearClick}
                disabled={!value}
                className={cn(
                  'text-xs font-medium px-2 py-1 rounded-md transition-colors',
                  'text-text-muted hover:text-text hover:bg-surface-alt',
                  'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-text-muted',
                  'focus:outline-none focus:ring-1 focus:ring-primary focus:ring-offset-1',
                )}
                data-testid={`${dataTestId || 'date-picker'}-clear`}
                aria-label={t('shared.datePicker.clearDate')}
              >
                {t('global.clear')}
              </button>
              <button
                type="button"
                onClick={handleTodayClick}
                disabled={isTodayDisabled()}
                className={cn(
                  'text-xs font-medium px-2 py-1 rounded-md transition-colors',
                  'text-primary hover:bg-primary/10',
                  'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent',
                  'focus:outline-none focus:ring-1 focus:ring-primary focus:ring-offset-1',
                )}
                data-testid={`${dataTestId || 'date-picker'}-today`}
                aria-label={t('shared.datePicker.selectToday')}
              >
                {t('global.today')}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};